import React, { useState, useContext } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { API_URL } from '../../utils/api';
import { Title, Description, FormGroup, Label, Input, TextArea, Button, SuccessMessage, ErrorMessage } from './ContactContent.styles';

function ContactContent({ onClose, tipo = 'contacto' }) {
  const esDenuncia = tipo === 'denuncia';
  const { theme } = useContext(ThemeContext);

  const [formData, setFormData] = useState({
    nombre: '', email: '', asunto: '', mensaje: '', url: '', quiereRespuesta: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validación
    if (!formData.mensaje) return setError('El mensaje es obligatorio');
    if (!esDenuncia && (!formData.nombre || !formData.email || !formData.asunto))
      return setError('Nombre, email y asunto son obligatorios');
    if (esDenuncia && formData.quiereRespuesta && !formData.email)
      return setError('Introduce un email para recibir respuesta');

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/email/enviar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo, ...formData }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al enviar');
      setSubmitted(true);
      setTimeout(() => { setSubmitted(false); if (onClose) onClose(); }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Title bgColor={theme.modalBg}>
        {esDenuncia ? '🚨 Reportar contenido' : 'Contáctanos'}
      </Title>
      <Description bgColor={theme.modalBg}>
        {esDenuncia
          ? 'Ayúdanos a mantener Trastero seguro. Puedes denunciar de forma anónima.'
          : '¿Tienes alguna pregunta? Nos encantaría escucharte.'}
      </Description>

      {submitted && (
        <SuccessMessage>
          ✓ {esDenuncia ? '¡Denuncia enviada! La revisaremos pronto.' : '¡Gracias! Nos pondremos en contacto pronto.'}
        </SuccessMessage>
      )}
      {error && <ErrorMessage>⚠ {error}</ErrorMessage>}

      <form onSubmit={handleSubmit}>

        {/* Nombre — obligatorio en contacto, opcional en denuncia */}
        <FormGroup>
          <Label bgColor={theme.modalBg} htmlFor="nombre">
            Nombre {esDenuncia ? '(opcional)' : 'Completo'}
          </Label>
          <Input type="text" id="nombre" name="nombre"
            value={formData.nombre} onChange={handleChange}
            placeholder={esDenuncia ? 'Anónimo' : 'Tu nombre'}
            accentColor={theme.accent} bgColor={theme.modalBg}
            title={esDenuncia ? 'Opcional — puedes denunciar de forma anónima.' : 'Tu nombre completo para que podamos identificarte.'} />
        </FormGroup>

        {/* Email — obligatorio en contacto, condicional en denuncia */}
        {!esDenuncia && (
          <FormGroup>
            <Label bgColor={theme.modalBg} htmlFor="email">Correo Electrónico</Label>
            <Input type="email" id="email" name="email"
              value={formData.email} onChange={handleChange}
              placeholder="tu@email.com"
              accentColor={theme.accent} bgColor={theme.modalBg}
              title="Te responderemos a este correo electrónico." />
          </FormGroup>
        )}

        {/* Asunto — solo en contacto */}
        {!esDenuncia && (
          <FormGroup>
            <Label bgColor={theme.modalBg} htmlFor="asunto">Asunto</Label>
            <Input type="text" id="asunto" name="asunto"
              value={formData.asunto} onChange={handleChange}
              placeholder="Asunto"
              accentColor={theme.accent} bgColor={theme.modalBg}
              title="Resume en pocas palabras el motivo de tu mensaje." />
          </FormGroup>
        )}

        {/* URL — solo en denuncia */}
        {esDenuncia && (
          <FormGroup>
            <Label bgColor={theme.modalBg} htmlFor="url">URL del contenido denunciado</Label>
            <Input type="url" id="url" name="url"
              value={formData.url} onChange={handleChange}
              placeholder="https://trasteroweb.es/de/nombre"
              accentColor={theme.accent} bgColor={theme.modalBg}
              title="Copia y pega la dirección web del artículo o trastero que quieres denunciar." />
          </FormGroup>
        )}

        {/* Mensaje */}
        <FormGroup>
          <Label bgColor={theme.modalBg} htmlFor="mensaje">
            {esDenuncia ? 'Describe el problema' : 'Mensaje'}
          </Label>
          <TextArea id="mensaje" name="mensaje"
            value={formData.mensaje} onChange={handleChange}
            placeholder={esDenuncia ? 'Explica el motivo de la denuncia...' : 'Cuéntanos tu pregunta...'}
            accentColor={theme.accent} bgColor={theme.modalBg}
            title={esDenuncia ? 'Describe con detalle el contenido inapropiado o el problema detectado.' : 'Explica tu consulta con el máximo detalle posible.'} />
        </FormGroup>

        {/* Checkbox quiere respuesta — solo en denuncia */}
        {esDenuncia && (
          <FormGroup>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" name="quiereRespuesta"
                checked={formData.quiereRespuesta} onChange={handleChange} />
              Quiero recibir respuesta sobre esta denuncia
            </label>
            {formData.quiereRespuesta && (
              <Input type="email" name="email" style={{ marginTop: 8 }}
                value={formData.email} onChange={handleChange}
                placeholder="tu@email.com"
                accentColor={theme.accent} bgColor={theme.modalBg} />
            )}
          </FormGroup>
        )}

        <Button type="submit" disabled={loading} accentColor={theme.accent}>
          {loading ? 'Enviando...' : esDenuncia ? '🚨 Enviar denuncia' : 'Enviar Mensaje'}
        </Button>
      </form>
    </>
  );
}

export default ContactContent;
